export const loadImage = (
  url: string,
  canvas = document.createElement('canvas')
): Promise<HTMLCanvasElement> => {
  return new Promise((resolve, reject): void => {
    const img = new Image();

    const errorHandler = (event: ErrorEvent): void => {
      reject(event);
    };

    img.setAttribute('crossOrigin', 'use');
    img.crossOrigin = 'use-credentials';

    img.onload = (): void => {
      img.removeEventListener('error', errorHandler);

      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');

      if (ctx) {
        ctx.drawImage(img, 0, 0);
      }

      resolve(canvas);
    };

    img.addEventListener('error', errorHandler);

    img.src = url;
  });
};

const loadTile = (url: string): Promise<CanvasImageSource> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`load ${url} fail`));
    img.setAttribute('crossOrigin', 'use');
    img.crossOrigin = 'use-credentials';
    img.src = url;
  });
};

export const scaleTileCanvas = (
  canvas: HTMLCanvasElement,
  canvasWidth: number,
  canvasHeight: number,
  zoom: number,
  maxZoom: number
): Promise<HTMLCanvasElement> => {
  return new Promise((resolve): void => {
    const sheetCanvasWidth = canvasWidth;
    const sheetCanvasHeight = canvasHeight;

    const sheetCanvas = document.createElement('canvas');
    sheetCanvas.width = sheetCanvasWidth;
    sheetCanvas.height = sheetCanvasHeight;

    const sheetCtx = sheetCanvas.getContext('2d');

    if (sheetCtx) {
      const zoomLevelCount = maxZoom;
      const scale = 2 ** (zoomLevelCount - zoom);
      sheetCtx.transform(scale, 0, 0, scale, 0, 0);
      sheetCtx.fillStyle = '#FFFFFF';
      sheetCtx.fillRect(0, 0, sheetCanvas.width, sheetCanvas.height);
      sheetCtx.drawImage(canvas, 0, 0);
    }
    resolve(sheetCanvas);
  });
};

export const getTiledCanvas = async (
  tileUrlTemplate: string,
  sheetWidth: number,
  sheetHeight: number,
  zoom: number,
  tileSize: number
): Promise<HTMLCanvasElement> => {
  return new Promise((resolve): void => {
    const scale = 2 ** (zoom - 1);

    const canvas = document.createElement('canvas');
    canvas.width = sheetWidth * scale;
    canvas.height = sheetHeight * scale;

    const tileCountX = Math.ceil((sheetWidth * scale) / tileSize);
    const tileCountY = Math.ceil((sheetHeight * scale) / tileSize);
    const promises: Array<Promise<void>> = [];
    for (let x = 0; x < tileCountX; x++) {
      for (let y = 0; y < tileCountY; y++) {
        const url = tileUrlTemplate
          .replace('{z}', zoom.toString())
          .replace('{x}', x.toString())
          .replace('{y}', y.toString());

        promises.push(
          loadTile(url).then((tile) => {
            const ctx = canvas.getContext('2d');

            if (ctx) {
              ctx.drawImage(tile, x * tileSize, y * tileSize);
            }
          })
        );
      }
    }
    Promise.all(promises).then(() => {
      resolve(canvas);
    });
  });
};

export enum ImageExtension {
  JPG = '.jpg',
  JPEG = '.jpeg',
  PNG = '.png',
  GIF = '.gif',
  SVG = '.svg',
  BMP = '.bmp',
  TIF = '.tif',
  TIFF = '.tiff',
}

export const hasImageExtension = (fileName: string): boolean => {
  return Object.values(ImageExtension).some((ext) =>
    fileName.toLowerCase().endsWith(ext)
  );
};

export const supportedImageExtensions = Object.values(ImageExtension).reduce(
  (extensionsStr, fileExt) => {
    if (extensionsStr.length === 0) return fileExt;
    return `${extensionsStr},${fileExt}`;
  },
  ''
);
